import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    Sun, Zap, Building2, Calculator, ArrowRight,
    Shield, Users, Award, Leaf, Star, ChevronRight,
    Home as HomeIcon, CheckCircle2, TrendingUp, UserCheck, BadgePercent, Maximize2,
    Phone, MapPin, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import ProductCard from '../components/ProductCard';
import ExpertVisitModal from '../components/ExpertVisitModal';
import { API } from '../App';
import { toast } from 'sonner';

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1500, suffix = '', prefix = '' }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    useEffect(() => {
        if (isInView) {
            let start = 0;
            const end = parseFloat(value);
            const incrementTime = duration / end;
            const timer = setInterval(() => {
                start += 1;
                setCount(start);
                if (start >= end) clearInterval(timer);
            }, Math.max(incrementTime, 10));
            return () => clearInterval(timer);
        }
    }, [isInView, value, duration]);

    return (
        <span ref={ref} className="font-mono">
            {prefix}{count.toLocaleString()}{suffix}
        </span>
    );
};

// Section Animation Wrapper
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-100px' });

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay }}
            className={className}
        >
            {children}
        </motion.div>
    );
};

const HomePage = () => {
    const navigate = useNavigate();
    const [homeProducts, setHomeProducts] = useState([]);
    const [commercialProducts, setCommercialProducts] = useState([]);
    const [calcForm, setCalcForm] = useState({
        monthly_bill: '',
        property_type: 'home',
        city: '',
        backup_required: false,
        roof_area: ''
    });
    const [calcResult, setCalcResult] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [showExpertModal, setShowExpertModal] = useState(false);
    const [heroSlideIndex, setHeroSlideIndex] = useState(0);

    const heroSlides = [
        {
            titlePart1: 'Rooftop Solar, ',
            titleHighlight: 'Zero Hassle',
            subtitle: 'Installation, subsidy paperwork & finance support — all handled by our experts at your doorstep.'
        },
        {
            titlePart1: 'We Handle ',
            titleHighlight: 'Everything',
            subtitle: 'Solar installation, government subsidy docs & finance arranged — sit back while we do the work.'
        },
        {
            titlePart1: 'Trusted Brands, ',
            titleHighlight: 'Expert Setup',
            subtitle: 'Top solar brands with professional installation. Subsidy & finance process fully managed for you.'
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setHeroSlideIndex(prev => (prev + 1) % heroSlides.length);
        }, 7000);
        return () => clearInterval(timer);
    }, [heroSlides.length]);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const [homeRes, commercialRes] = await Promise.all([
                axios.get(`${API}/api/products?category=home&limit=4`),
                axios.get(`${API}/api/products?category=commercial&limit=4`)
            ]);
            setHomeProducts(homeRes.data);
            setCommercialProducts(commercialRes.data);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        }
    };

    const handleCalculate = async () => {
        if (!calcForm.monthly_bill || !calcForm.city) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsCalculating(true);
        try {
            const response = await axios.post(`${API}/api/calculator`, {
                monthly_bill: parseFloat(calcForm.monthly_bill),
                property_type: calcForm.property_type,
                city: calcForm.city,
                backup_required: calcForm.backup_required,
                roof_area: calcForm.roof_area ? parseFloat(calcForm.roof_area) : null
            });
            setCalcResult(response.data);
        } catch (error) {
            toast.error('Calculation failed. Please try again.');
        } finally {
            setIsCalculating(false);
        }
    };

    const testimonials = [
        {
            name: 'Rajesh Kumar',
            role: 'Homeowner, Delhi',
            content: 'SolarSavers helped us reduce our electricity bill by 80%. The installation was seamless and the support team is always responsive.',
            rating: 5,
            image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'
        },
        {
            name: 'Priya Sharma',
            role: 'Business Owner, Mumbai',
            content: 'We installed a 50kW commercial system for our factory. ROI was achieved in just 3 years. Highly recommend SolarSavers!',
            rating: 5,
            image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'
        },
        {
            name: 'Amit Patel',
            role: 'Architect, Bangalore',
            content: 'The team at SolarSavers understands both aesthetics and functionality. Our clients love the solar installations we recommend.',
            rating: 5,
            image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100'
        }
    ];

    const benefits = [
        {
            icon: Zap,
            title: 'High Efficiency Panels',
            description: 'Industry-leading solar panels with 22%+ efficiency ratings for maximum power generation.'
        },
        {
            icon: Users,
            title: 'Trusted Vendors',
            description: 'Verified and certified vendors ensuring quality products and reliable service.'
        },
        {
            icon: Award,
            title: 'Government Subsidy Guidance',
            description: 'Expert assistance to help you avail maximum government subsidies and benefits.'
        },
        {
            icon: Shield,
            title: 'Warranty & Support',
            description: '25-year panel warranty with dedicated after-sales support and maintenance.'
        }
    ];

    const partnerLogos = [
        { name: 'Adani Solar', img: '/assets/brands/adani.png' },
        { name: 'Waaree', img: '/assets/brands/waaree.png' },
        { name: 'RenewSys', img: '/assets/brands/renewsys.png' },
        { name: 'Vikram Solar', img: '/assets/brands/vikram.png' },
        { name: 'TATA Solar', img: '/assets/brands/tata.png' },
        { name: 'UTL', img: '/assets/brands/utl.png' },
        { name: 'Eastman', img: '/assets/brands/eastman.png' },
        { name: 'Luminous', img: '/assets/brands/luminous.webp' },
        { name: 'Sukam', img: '/assets/brands/sukam.png' }
    ];

    return (
        <div className="min-h-screen">
            {/* ── MOBILE-ONLY HERO (hidden on md+) ────────────────── */}
            <div className="md:hidden">
                {/* Full-bleed hero banner with background image */}
                <div className="mobile-hero-banner">
                    {/* Background image + gradient overlay */}
                    <div className="mobile-hero-bg">
                        <img
                            src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800&q=80"
                            alt=""
                            className="mobile-hero-bg-img"
                        />
                        <div className="mobile-hero-overlay" />
                    </div>

                    {/* Hero content — centered */}
                    <div className="mobile-hero-content">
                        {/* Brand pill */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-full backdrop-blur-md mb-4"
                        >
                            <Sun className="w-4 h-4 text-primary" />
                            <span className="text-xs text-white/90 font-semibold tracking-wide">India's #1 Solar Marketplace</span>
                        </motion.div>

                        {/* Headline — rotating */}
                        <div style={{ minHeight: '120px' }}>
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={heroSlideIndex}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -15 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <h1 className="text-[1.6rem] font-extrabold leading-[1.15] text-white mb-2">
                                        {heroSlides[heroSlideIndex].titlePart1}
                                        <span className="solar-text-gradient">{heroSlides[heroSlideIndex].titleHighlight}</span>
                                    </h1>
                                    <p className="text-sm text-white/75 mb-5 max-w-[300px] leading-relaxed">
                                        {heroSlides[heroSlideIndex].subtitle}
                                    </p>
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* CTA */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <button
                                onClick={() => setShowExpertModal(true)}
                                className="flex items-center gap-2 bg-primary text-primary-foreground font-bold text-sm px-6 py-3 rounded-full shadow-lg shadow-primary/40 active:scale-95 transition-transform"
                            >
                                <UserCheck className="w-4 h-4" />
                                Book Free Expert Visit
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <p className="text-[11px] text-white/50 mt-2 ml-1">Get a personalized solar assessment at your doorstep</p>
                        </motion.div>
                    </div>
                </div>

                {/* Quick actions row (Zomato-style pills) */}
                <div className="mobile-quick-actions">
                    {[
                        { icon: Calculator, label: 'Calculator', path: '/calculator' },
                        { icon: Sun, label: 'Subsidy', path: '/subsidy' },
                        { icon: Building2, label: 'Commercial', path: '/shop?category=commercial' },
                        { icon: UserCheck, label: 'Expert Visit', action: () => setShowExpertModal(true) },
                    ].map((item, i) => (
                        item.action ? (
                            <button key={i} onClick={item.action} className="mobile-quick-action-item">
                                <div className="mobile-quick-action-icon">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <span>{item.label}</span>
                            </button>
                        ) : (
                            <Link key={i} to={item.path} className="mobile-quick-action-item">
                                <div className="mobile-quick-action-icon">
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <span>{item.label}</span>
                            </Link>
                        )
                    ))}
                </div>

                {/* Stats strip — floating pill style */}
                <div className="mobile-stats-strip">
                    {[
                        { value: '5,000+', label: 'Customers' },
                        { value: '50MW+', label: 'Installed' },
                        { value: '₹78K', label: 'Max Subsidy' },
                    ].map((stat, i) => (
                        <div key={i} className="mobile-stat-item">
                            <p className="text-sm font-bold text-primary">{stat.value}</p>
                            <p className="text-[10px] text-muted-foreground">{stat.label}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── DESKTOP-ONLY HERO (hidden on mobile) ────────────────── */}
            <section className="relative hidden md:flex items-center min-h-screen overflow-hidden hero-bg pt-20">
                {/* Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
                        className="absolute top-20 right-20 opacity-10"
                    >
                        <Sun className="w-64 h-64 text-primary" />
                    </motion.div>
                </div>

                <div className="container-solar relative z-10 py-20">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="text-white space-y-6">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full backdrop-blur-sm"
                            >
                                <Leaf className="w-4 h-4 text-accent" />
                                <span className="text-sm">Clean Energy for a Better Tomorrow</span>
                            </motion.div>

                            <div style={{ minHeight: '180px' }}>
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={heroSlideIndex}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.6 }}
                                    >
                                        <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-4">
                                            {heroSlides[heroSlideIndex].titlePart1}
                                            <span className="solar-text-gradient">{heroSlides[heroSlideIndex].titleHighlight}</span>
                                        </h1>
                                        <p className="text-xl text-white/80 max-w-lg">
                                            {heroSlides[heroSlideIndex].subtitle}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* Slide indicators */}
                            <div className="flex items-center gap-2 mt-2">
                                {heroSlides.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setHeroSlideIndex(i)}
                                        className={`rounded-full transition-all duration-300 ${i === heroSlideIndex ? 'w-8 h-2 bg-primary' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`}
                                    />
                                ))}
                            </div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                className="flex flex-wrap gap-4"
                            >
                                <Button
                                    onClick={() => setShowExpertModal(true)}
                                    className="btn-primary text-lg px-8 py-6"
                                >
                                    <UserCheck className="w-5 h-5 mr-2" />
                                    Book Free Expert Visit
                                </Button>
                                <Link to="/shop">
                                    <Button variant="outline" className="text-lg px-8 py-6 border-white/30 text-white hover:bg-white/10">
                                        View Products
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </Button>
                                </Link>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.6 }}
                                className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10"
                            >
                                {[
                                    { value: 5000, suffix: '+', label: 'Happy Customers' },
                                    { value: 50, suffix: 'MW+', label: 'Solar Installed' },
                                    { value: 100, suffix: '+', label: 'Cities Served' },
                                ].map((s, i) => (
                                    <div key={i}>
                                        <div className="text-3xl font-bold text-primary">
                                            <AnimatedCounter value={s.value} suffix={s.suffix} />
                                        </div>
                                        <p className="text-white/60 text-sm">{s.label}</p>
                                    </div>
                                ))}
                            </motion.div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                            className="relative"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600"
                                alt="Solar Panels"
                                className="rounded-2xl shadow-2xl"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-card p-4 rounded-xl shadow-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                                        <TrendingUp className="w-6 h-6 text-accent" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">Average Savings</p>
                                        <p className="text-xl font-bold">₹15,000/month</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Wave Divider */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 120" className="w-full h-20 fill-background">
                        <path d="M0,40 C480,120 960,0 1440,80 L1440,120 L0,120 Z" />
                    </svg>
                </div>
            </section>

            {/* Solar Calculator Section */}
            <section className="pt-3 pb-6 md:py-24 md:-mt-10 relative z-10">
                <div className="container-solar">
                    <AnimatedSection>
                        <div className="calculator-card max-w-4xl mx-auto">
                            <div className="text-center mb-8">
                                <h2 className="section-title">
                                    <Calculator className="w-8 h-8 inline-block mr-2 text-primary" />
                                    Solar Calculator
                                </h2>
                                <p className="section-subtitle">Find out how much you can save with solar energy</p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-8">
                                {/* Calculator Form */}
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <Label htmlFor="monthly_bill">Monthly Electricity Bill (₹)</Label>
                                        <Input
                                            id="monthly_bill"
                                            type="number"
                                            placeholder="e.g., 5000"
                                            value={calcForm.monthly_bill}
                                            onChange={(e) => setCalcForm({ ...calcForm, monthly_bill: e.target.value })}
                                            className="input-solar"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Property Type</Label>
                                        <Select
                                            value={calcForm.property_type}
                                            onValueChange={(value) => setCalcForm({ ...calcForm, property_type: value })}
                                        >
                                            <SelectTrigger className="input-solar">
                                                <SelectValue placeholder="Select property type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="home">
                                                    <div className="flex items-center gap-2">
                                                        <HomeIcon className="w-4 h-4" />
                                                        Home / Residential
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="commercial">
                                                    <div className="flex items-center gap-2">
                                                        <Building2 className="w-4 h-4" />
                                                        Commercial / Industrial
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="city">City / State</Label>
                                        <Input
                                            id="city"
                                            type="text"
                                            placeholder="e.g., Delhi"
                                            value={calcForm.city}
                                            onChange={(e) => setCalcForm({ ...calcForm, city: e.target.value })}
                                            className="input-solar"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="home_roof_area">Available Roof Area (sq. ft.) <span className="text-muted-foreground text-xs font-normal">(Optional)</span></Label>
                                        <div className="relative">
                                            <Maximize2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="home_roof_area"
                                                type="number"
                                                placeholder="e.g., 500"
                                                value={calcForm.roof_area}
                                                onChange={(e) => setCalcForm({ ...calcForm, roof_area: e.target.value })}
                                                className="input-solar pl-10"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">Rule of thumb: ~100 sq. ft. per 1 kWp of solar capacity</p>
                                    </div>

                                    <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
                                        <div>
                                            <Label htmlFor="backup">Battery Backup Required</Label>
                                            <p className="text-sm text-muted-foreground">For power during outages</p>
                                        </div>
                                        <Switch
                                            id="backup"
                                            checked={calcForm.backup_required}
                                            onCheckedChange={(checked) => setCalcForm({ ...calcForm, backup_required: checked })}
                                        />
                                    </div>

                                    <Button
                                        onClick={handleCalculate}
                                        disabled={isCalculating}
                                        className="w-full btn-primary py-6"
                                    >
                                        {isCalculating ? 'Calculating...' : 'Calculate Savings'}
                                    </Button>
                                </div>

                                {/* Results */}
                                <div className="bg-secondary/30 rounded-2xl p-6 space-y-6">
                                    <h3 className="font-semibold text-lg">Your Solar Estimate</h3>
                                    <p className="text-xs text-muted-foreground bg-accent/10 p-2 rounded border border-accent/20">
                                        Note: These are approximate values designed to give you a basic estimate. For accurate sizing and details, please request an expert visit.
                                    </p>

                                    {calcResult ? (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            className="space-y-4"
                                        >
                                            <div className="p-4 bg-card rounded-xl border border-border">
                                                <p className="text-sm text-muted-foreground">Recommended System Size</p>
                                                <p className="text-3xl font-bold text-primary animate-count-up">
                                                    {Math.round(calcResult.recommended_size_kw)} kW
                                                </p>
                                            </div>

                                            <div className="p-4 bg-card rounded-xl border border-border">
                                                <p className="text-sm text-muted-foreground">Estimated Cost</p>
                                                <p className="text-3xl font-bold animate-count-up">
                                                    ₹{calcResult.estimated_cost?.toLocaleString()}
                                                </p>
                                            </div>

                                            <div className="p-4 bg-accent/10 rounded-xl border border-accent/30">
                                                <p className="text-sm text-accent">Annual Savings</p>
                                                <p className="text-3xl font-bold text-accent animate-count-up">
                                                    ₹{calcResult.annual_savings?.toLocaleString()}
                                                </p>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 text-sm">
                                                <div className="p-3 bg-card rounded-lg">
                                                    <p className="text-muted-foreground">Payback Period</p>
                                                    <p className="font-semibold">{calcResult.payback_years} years</p>
                                                </div>
                                                <div className="p-3 bg-card rounded-lg">
                                                    <p className="text-muted-foreground">CO₂ Reduction</p>
                                                    <p className="font-semibold">{calcResult.co2_reduction_kg} kg/year</p>
                                                </div>
                                            </div>

                                            {calcForm.roof_area && (
                                                <div className={`p-3 rounded-lg border ${parseFloat(calcForm.roof_area) >= calcResult.recommended_size_kw * 100 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                                    <div className="flex items-center gap-2">
                                                        <Maximize2 className="w-4 h-4" />
                                                        <p className="text-sm font-medium">Space Required: ~{calcResult.recommended_size_kw * 100} sq. ft.</p>
                                                    </div>
                                                    <p className={`text-xs mt-1 ${parseFloat(calcForm.roof_area) >= calcResult.recommended_size_kw * 100 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                        {parseFloat(calcForm.roof_area) >= calcResult.recommended_size_kw * 100
                                                            ? `✓ Your ${calcForm.roof_area} sq. ft. roof is sufficient`
                                                            : `✗ Your ${calcForm.roof_area} sq. ft. roof may need a smaller system (~${Math.floor(parseFloat(calcForm.roof_area) / 100)} kW max)`
                                                        }
                                                    </p>
                                                </div>
                                            )}

                                            <Button
                                                onClick={() => navigate(`/shop?minSize=${Math.max(0, calcResult.recommended_size_kw - 2)}&maxSize=${calcResult.recommended_size_kw + 3}&recommendedKw=${calcResult.recommended_size_kw}`)}
                                                className="w-full"
                                                variant="outline"
                                            >
                                                View Recommended Products
                                                <ChevronRight className="w-4 h-4 ml-2" />
                                            </Button>
                                        </motion.div>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-12">
                                            <Sun className="w-16 h-16 mb-4 text-primary/30" />
                                            <p>Enter your details to see personalized solar recommendations</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Home Solar Products */}
            <section className="py-6 md:py-24 bg-secondary/30">
                <div className="container-solar">
                    <AnimatedSection>
                        <div className="flex items-center justify-between mb-5 md:mb-12">
                            <div>
                                <h2 className="section-title flex items-center gap-3">
                                    <HomeIcon className="w-8 h-8 text-primary" />
                                    Solar Solutions for Homes
                                </h2>
                                <p className="section-subtitle">Premium solar systems for residential properties (1kW - 10kW)</p>
                            </div>
                            <Link to="/shop/home" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
                                View All <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                            {homeProducts.map((product, index) => (
                                <ProductCard key={product.id} product={product} index={index} />
                            ))}
                        </div>

                        <div className="mt-8 text-center md:hidden">
                            <Link to="/shop/home">
                                <Button variant="outline">View All Home Solar</Button>
                            </Link>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Commercial Solar Products */}
            <section className="py-6 md:py-24">
                <div className="container-solar">
                    <AnimatedSection>
                        <div className="flex items-center justify-between mb-5 md:mb-12">
                            <div>
                                <h2 className="section-title flex items-center gap-3">
                                    <Building2 className="w-8 h-8 text-primary" />
                                    Commercial & Industrial Solar
                                </h2>
                                <p className="section-subtitle">High-capacity solar solutions for businesses (10kW - 100kW+)</p>
                            </div>
                            <Link to="/shop/commercial" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
                                View All <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
                            {commercialProducts.map((product, index) => (
                                <ProductCard key={product.id} product={product} index={index} />
                            ))}
                        </div>

                        <div className="mt-8 text-center md:hidden">
                            <Link to="/shop/commercial">
                                <Button variant="outline">View All Commercial Solar</Button>
                            </Link>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* PM Surya Ghar Subsidy Section */}
            <section className="py-8 md:py-20 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 right-20 w-40 h-40 bg-white rounded-full blur-2xl" />
                    <div className="absolute bottom-20 left-20 w-60 h-60 bg-white rounded-full blur-3xl" />
                </div>

                <div className="container-solar relative z-10">
                    <AnimatedSection>
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div className="text-white space-y-6">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
                                    <Sun className="w-5 h-5" />
                                    <span className="text-sm font-medium">Government of India</span>
                                </div>

                                <h2 className="text-3xl md:text-4xl font-bold">
                                    PM Surya Ghar<br />
                                    Muft Bijli Yojana
                                </h2>

                                <p className="text-white/80 text-lg">
                                    Get up to <strong className="text-yellow-300">₹78,000 subsidy</strong> from the
                                    Central Government on rooftop solar installation for your home.
                                </p>

                                <div className="grid grid-cols-3 gap-4 py-4">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-yellow-300">₹30K</p>
                                        <p className="text-xs text-white/70">per kW (upto 2kW)</p>
                                    </div>
                                    <div className="text-center border-x border-white/20">
                                        <p className="text-2xl font-bold text-yellow-300">₹78K</p>
                                        <p className="text-xs text-white/70">Max Subsidy</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-yellow-300">Free</p>
                                        <p className="text-xs text-white/70">Electricity</p>
                                    </div>
                                </div>

                                <Link to="/subsidy">
                                    <Button className="bg-white text-green-600 hover:bg-white/90">
                                        <BadgePercent className="w-5 h-5 mr-2" />
                                        Explore Subsidy Scheme
                                    </Button>
                                </Link>
                            </div>

                            <div className="hidden md:block">
                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                                    <h3 className="text-xl font-bold text-white mb-6">Who Can Apply?</h3>
                                    <ul className="space-y-3">
                                        {[
                                            'Indian citizen with Aadhaar',
                                            'Own house with electricity connection',
                                            'Residential property only',
                                            'Grid-connected system'
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-center gap-3 text-white/90">
                                                <CheckCircle2 className="w-5 h-5 text-green-300 shrink-0" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-6 pt-6 border-t border-white/20">
                                        <p className="text-white/70 text-sm">
                                            Documents required: Aadhaar, Electricity Bill, Bank Details
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Why Solar Savers */}
            <section className="py-6 md:py-24 bg-secondary/30">
                <div className="container-solar">
                    <AnimatedSection>
                        <div className="text-center mb-6 md:mb-14">
                            <h2 className="section-title mb-3">
                                Why <span className="solar-text-gradient">Solar Savers</span>?
                            </h2>
                            <p className="section-subtitle max-w-2xl mx-auto">
                                Go solar without the hassle — relax at home while our experts handle everything.
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-8 md:gap-14 items-start">
                            {/* Left: Story & Mission */}
                            <div className="space-y-5">
                                <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                                    Solar Savers is a team of professionals with <strong className="text-foreground">50+ years of cumulative experience</strong> in rooftop solar installation and clean energy solutions. The initiative is led by <strong className="text-foreground">Mr. John Gerber</strong>, a global advocate for carbon reduction with extensive experience in renewable energy and sustainability.
                                </p>
                                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 md:p-5">
                                    <div className="flex items-start gap-3">
                                        <Leaf className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-sm md:text-base mb-1">Our Mission</p>
                                            <p className="text-muted-foreground text-sm leading-relaxed">
                                                To make the earth more livable by reducing carbon emissions and promoting clean solar energy. Through solar adoption, our goal is to impact <strong className="text-foreground">more than one billion lives</strong> by reducing millions of metric tons of CO₂ emissions.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-accent/5 border border-accent/20 rounded-xl p-4 md:p-5">
                                    <div className="flex items-start gap-3">
                                        <TrendingUp className="w-6 h-6 text-accent shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-semibold text-sm md:text-base mb-1">Real Impact</p>
                                            <p className="text-muted-foreground text-sm leading-relaxed">
                                                A 3 kW rooftop solar system can reduce approximately <strong className="text-foreground">4,300–4,500 kWh</strong> of carbon-generating electricity annually.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 pt-2">
                                    <div className="text-center bg-card border rounded-xl p-3 md:p-4">
                                        <div className="w-10 h-10 md:w-12 md:h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                            <Award className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                                        </div>
                                        <p className="font-bold text-sm md:text-base">50+ Years</p>
                                        <p className="text-xs text-muted-foreground">Experience</p>
                                    </div>
                                    <div className="text-center bg-card border rounded-xl p-3 md:p-4">
                                        <div className="w-10 h-10 md:w-12 md:h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                            <Users className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                                        </div>
                                        <p className="font-bold text-sm md:text-base">1B+ Lives</p>
                                        <p className="text-xs text-muted-foreground">Impact Goal</p>
                                    </div>
                                    <div className="text-center bg-card border rounded-xl p-3 md:p-4">
                                        <div className="w-10 h-10 md:w-12 md:h-12 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-2">
                                            <Zap className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                                        </div>
                                        <p className="font-bold text-sm md:text-base">4,500 kWh</p>
                                        <p className="text-xs text-muted-foreground">CO₂ Saved/yr</p>
                                    </div>
                                </div>
                            </div>

                            {/* Right: What You Get */}
                            <div>
                                <h3 className="font-bold text-lg md:text-xl mb-4 md:mb-6">With Solar Savers, you receive:</h3>
                                <div className="space-y-3 md:space-y-4">
                                    {[
                                        { icon: Shield, title: 'Trusted Solar Brands', desc: 'We partner only with top-tier, certified solar panel and inverter manufacturers.' },
                                        { icon: Zap, title: 'Expert Installation', desc: 'Professional rooftop solar installation by our experienced technicians.' },
                                        { icon: Award, title: 'Complete Subsidy Support', desc: 'End-to-end documentation for PM Surya Ghar Yojana government subsidy.' },
                                        { icon: Calculator, title: 'Finance Assistance', desc: 'Need financing? We help arrange easy solar loans and EMI options.' }
                                    ].map((item, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: i * 0.1 }}
                                            className="flex items-start gap-4 bg-card border rounded-xl p-4 hover:border-primary/50 transition-colors group"
                                        >
                                            <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                                                <item.icon className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-sm md:text-base mb-0.5">{item.title}</h4>
                                                <p className="text-muted-foreground text-xs md:text-sm">{item.desc}</p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                <Button
                                    onClick={() => setShowExpertModal(true)}
                                    className="btn-primary w-full mt-5 md:mt-6 py-5 text-sm md:text-base"
                                >
                                    <UserCheck className="w-5 h-5 mr-2" />
                                    Book Free Expert Visit
                                </Button>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>


            {/* Simple Steps to Go Solar */}
            <section className="py-6 md:py-24">
                <div className="container-solar">
                    <AnimatedSection>
                        <div className="text-center mb-5 md:mb-12">
                            <h2 className="section-title">Simple Steps to Go <span className="solar-text-gradient">Solar</span></h2>
                            <p className="section-subtitle max-w-2xl mx-auto">
                                From inquiry to installation — we make going solar effortless.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[
                                { step: 1, icon: Phone, title: 'Free Consultation', desc: 'Once we receive your inquiry, our team contacts you for a free solar consultation.' },
                                { step: 2, icon: MapPin, title: 'Site Assessment', desc: 'Our experts inspect your rooftop and recommend the most suitable solar system.' },
                                { step: 3, icon: Zap, title: 'Professional Installation', desc: 'Our trained technicians install the solar system using trusted brands.' },
                                { step: 4, icon: FileText, title: 'Subsidy & Net Metering', desc: 'We manage PM Surya Ghar subsidy documentation and net metering approval.' }
                            ].map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="bg-card p-6 rounded-2xl border border-border hover:border-primary/50 transition-colors group"
                                >
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                            <item.icon className="w-7 h-7 text-primary" />
                                        </div>
                                        <span className="text-xs font-bold text-primary bg-primary/10 px-2.5 py-1 rounded-full">Step {item.step}</span>
                                    </div>
                                    <h3 className="font-semibold text-lg mb-2">{item.title}</h3>
                                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-6 md:py-24 bg-secondary/30">
                <div className="container-solar">
                    <AnimatedSection>
                        <div className="text-center mb-5 md:mb-12">
                            <h2 className="section-title">What Our Customers Say</h2>
                            <p className="section-subtitle">Join thousands of satisfied customers who made the switch to solar</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            {testimonials.map((testimonial, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="testimonial-card"
                                >
                                    <div className="flex items-center gap-1 mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star key={i} className={`w-4 h-4 ${i < testimonial.rating ? 'fill-primary text-primary' : 'text-muted'}`} />
                                        ))}
                                    </div>
                                    <p className="text-muted-foreground mb-6 leading-relaxed">"{testimonial.content}"</p>
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={testimonial.image}
                                            alt={testimonial.name}
                                            className="w-12 h-12 rounded-full object-cover"
                                        />
                                        <div>
                                            <p className="font-semibold">{testimonial.name}</p>
                                            <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Partner Brands - Infinite Marquee */}
            <section className="py-8 md:py-14 border-t border-border overflow-hidden bg-background">
                <div className="container-solar mb-6">
                    <AnimatedSection>
                        <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground/70">Trusted by India's Leading Solar Brands</p>
                    </AnimatedSection>
                </div>

                <div className="relative w-full overflow-hidden py-4 marquee-wrapper">
                    {/* Edge fade masks */}
                    <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 md:w-40 z-10" style={{background: 'linear-gradient(to right, hsl(var(--background)), transparent)'}} />
                    <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 md:w-40 z-10" style={{background: 'linear-gradient(to left, hsl(var(--background)), transparent)'}} />

                    {/* Scrolling track — 4 copies so each half is wider than any screen, seam always off-screen */}
                    <div className="flex animate-marquee" style={{width: 'max-content'}}>
                        {[...partnerLogos, ...partnerLogos, ...partnerLogos, ...partnerLogos].map((brand, index) => (
                            <div
                                key={index}
                                className="flex-none flex items-center justify-center mx-6 md:mx-10 grayscale hover:grayscale-0 transition-all duration-300 opacity-50 hover:opacity-100"
                                style={{width: '130px'}}
                                title={brand.name}
                            >
                                <img
                                    src={brand.img}
                                    alt={brand.name}
                                    className="max-h-14 w-auto object-contain select-none"
                                    draggable={false}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.display = 'none';
                                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                    }}
                                />
                                <span
                                    className="hidden font-extrabold text-base tracking-widest text-muted-foreground/60 whitespace-nowrap"
                                >
                                    {brand.name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Expert Visit CTA Section */}
            <section className="py-8 md:py-24 bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-500 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
                    <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
                    <div className="absolute top-1/2 left-1/3 w-20 h-20 border-2 border-white rounded-full" />
                </div>

                <div className="container-solar relative z-10">
                    <AnimatedSection>
                        <div className="max-w-4xl mx-auto">
                            <div className="grid md:grid-cols-2 gap-8 items-center">
                                <div className="text-white space-y-6">
                                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full backdrop-blur-sm">
                                        <UserCheck className="w-5 h-5" />
                                        <span className="text-sm font-medium">Free Expert Consultation</span>
                                    </div>

                                    <h2 className="text-3xl md:text-4xl font-bold">
                                        Not Sure Which Solar System is Right for You?
                                    </h2>

                                    <p className="text-white/80 text-lg">
                                        Request a free home visit from our certified solar experts. They'll assess your
                                        property, analyze your energy needs, and recommend the perfect solar solution.
                                    </p>

                                    <ul className="space-y-3">
                                        {[
                                            'Free site inspection & shading analysis',
                                            'Customized system design for your property',
                                            'Accurate cost and savings estimate',
                                            'Government subsidy guidance'
                                        ].map((item, i) => (
                                            <li key={i} className="flex items-center gap-3 text-white/90">
                                                <CheckCircle2 className="w-5 h-5 text-yellow-200 shrink-0" />
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                                    <div className="text-center text-white space-y-4">
                                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto">
                                            <UserCheck className="w-10 h-10" />
                                        </div>
                                        <h3 className="text-2xl font-bold">Request Expert Visit</h3>
                                        <p className="text-white/80">
                                            Our solar expert will visit your home within 2-3 business days
                                        </p>
                                        <Button
                                            onClick={() => setShowExpertModal(true)}
                                            className="w-full bg-white text-amber-600 hover:bg-white/90 py-6 text-lg font-semibold"
                                        >
                                            <UserCheck className="w-5 h-5 mr-2" />
                                            Schedule Free Visit
                                        </Button>
                                        <p className="text-xs text-white/60">
                                            No obligations • 100% Free • Available in 100+ cities
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-8 md:py-24 bg-gradient-to-r from-primary to-primary/80">
                <div className="container-solar text-center">
                    <AnimatedSection>
                        <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
                            Ready to Go Solar?
                        </h2>
                        <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
                            Start your solar journey today and join thousands of satisfied customers
                            saving money while saving the planet.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link to="/calculator">
                                <Button className="bg-white text-primary hover:bg-white/90 px-8 py-6 text-lg">
                                    Get Free Quote
                                </Button>
                            </Link>
                            <Link to="/contact">
                                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg">
                                    Contact Us
                                </Button>
                            </Link>
                        </div>
                    </AnimatedSection>
                </div>
            </section>

            {/* Expert Visit Modal */}
            <ExpertVisitModal
                isOpen={showExpertModal}
                onClose={() => setShowExpertModal(false)}
            />
        </div>
    );
};

export default HomePage;
